import React from 'react'
import { Button, Popconfirm, Table, Tooltip } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { QuestionCircleOutlined } from '@ant-design/icons'
import moment from 'moment'
import { adminEmployeesApi } from 'api/adminEmployees'
import { IAdminAccessRow, IAdminEmployeeAccess } from 'models/adminEmployees'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import styles from './Employees.module.scss'

const ACCESS_HINT =
  'Адреса и браузеры, с которых администратор работал в панели. При увольнении они попадают в чёрный список: с них панель закрыта для любого аккаунта. Блокировка закрывает браузер и его внешние адреса; адреса внутренней сети не блокируются.'

const renderDate = (value: string | null) =>
  value ? (
    <>
      <div>{moment(value).format('DD.MM.YYYY')}</div>
      <div>{moment(value).format('HH:mm')}</div>
    </>
  ) : (
    '—'
  )

const rowKey = ({ device_id, ips }: IAdminAccessRow) =>
  device_id ?? `ip:${ips[0]}`

const renderIps = (ips: string[]) => {
  if (ips.length < 2) return ips[0] ?? '—'
  return (
    <Tooltip
      title={ips.map((ip) => (
        <div key={ip}>{ip}</div>
      ))}
    >
      <span className={styles.multipleIps}>Несколько адресов</span>
    </Tooltip>
  )
}

interface IAdminAccessLogProps {
  employeeId: string
}

export const AdminAccessLog: React.FC<IAdminAccessLogProps> = ({
  employeeId
}) => {
  const [access, setAccess] = React.useState<IAdminEmployeeAccess | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [actingKey, setActingKey] = React.useState<string | null>(null)

  const loadAccess = React.useCallback(async () => {
    try {
      setIsLoading(true)
      const { data } = await adminEmployeesApi.getAccess(employeeId)
      setAccess(data)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }, [employeeId])

  React.useEffect(() => {
    setAccess(null)
    loadAccess()
  }, [loadAccess])

  const runAction = async (row: IAdminAccessRow, isBlocking: boolean) => {
    try {
      setActingKey(rowKey(row))
      const { data } = isBlocking
        ? await adminEmployeesApi.block(employeeId, row)
        : await adminEmployeesApi.unblock(employeeId, row)
      setAccess(data)
      showMessage(
        'success',
        isBlocking ? 'Заблокировано' : 'Разблокировано',
        isBlocking
          ? 'С этого браузера и его адресов панель закрыта'
          : 'Запись вернулась в журнал входов'
      )
    } catch (e) {
      showServerError(e)
    } finally {
      setActingKey(null)
    }
  }

  const renderAction = (row: IAdminAccessRow, isBlacklist: boolean) => {
    const isLoadingRow = actingKey === rowKey(row)
    if (isBlacklist) {
      return (
        <Popconfirm
          title='Разблокировать?'
          description='Браузер и его адреса снова смогут входить в панель.'
          okText='Разблокировать'
          cancelText='Отмена'
          onConfirm={() => runAction(row, false)}
        >
          <Button size='small' loading={isLoadingRow}>
            Разблокировать
          </Button>
        </Popconfirm>
      )
    }
    return (
      <Popconfirm
        title='Заблокировать?'
        description={
          <>
            <div>Закроются браузер и его внешние адреса — для любого аккаунта.</div>
            <div>Адреса внутренней сети останутся открытыми.</div>
          </>
        }
        okText='Заблокировать'
        cancelText='Отмена'
        okButtonProps={{ danger: true }}
        onConfirm={() => runAction(row, true)}
      >
        <Button
          size='small'
          className={styles.blockButton}
          loading={isLoadingRow}
        >
          Заблокировать
        </Button>
      </Popconfirm>
    )
  }

  const getColumns = (isBlacklist: boolean): ColumnsType<IAdminAccessRow> => [
    {
      title: 'Браузер',
      dataIndex: 'browser',
      render: (_, { browser, user_agent, device_id }) => (
        <Tooltip title={user_agent || undefined}>
          <div>{browser ?? '—'}</div>
          {device_id && (
            <div className={styles.accessSubText}>
              ID {device_id.slice(0, 8)}
            </div>
          )}
        </Tooltip>
      )
    },
    {
      title: 'Адрес',
      dataIndex: 'ips',
      render: renderIps
    },
    {
      title: 'Первый вход',
      dataIndex: 'first_at',
      render: renderDate
    },
    {
      title: 'Последний вход',
      dataIndex: 'last_at',
      render: renderDate
    },
    {
      title: 'Действие',
      key: 'action',
      render: (_, row) => renderAction(row, isBlacklist)
    }
  ]

  const renderTable = (rows: IAdminAccessRow[], isBlacklist: boolean) => (
    <Table
      size='small'
      scroll={{ x: 640 }}
      columns={getColumns(isBlacklist)}
      dataSource={rows}
      loading={isLoading}
      rowKey={rowKey}
      pagination={{ pageSize: 5, hideOnSinglePage: true }}
      locale={{
        emptyText: isBlacklist ? 'Заблокированных нет' : 'Входов пока нет'
      }}
    />
  )

  return (
    <div className={styles.accessSection}>
      <div className={styles.labelWithHelp}>
        <span className={styles.accessTitle}>Входы в панель</span>
        <Tooltip title={ACCESS_HINT}>
          <QuestionCircleOutlined className={styles.helpIcon} />
        </Tooltip>
      </div>
      {renderTable(access?.log ?? [], false)}
      <span className={styles.accessTitle}>Чёрный список</span>
      {renderTable(access?.blacklist ?? [], true)}
    </div>
  )
}
